import type { ReactElement, SubmitEvent } from "react";
import { useParams } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { FormSummary } from "../../forms/fields.js";

import { LoadingPage } from "./loading-page.js";
import { tenantApi, tenantUrls } from "./urls.js";
import { useSend } from "./use-send.js";

/** 解約の確認に出すもの（ApiTenantCancellation）。 */
export interface TenantCancellation {
    readonly tenantId: number;
    readonly title: string;
    /** 注意書き（平文）。<b>HTML にしない</b>——改行は CSS で見せる。 */
    readonly confirmText: string;
}

const title = "解約";

/**
 * 最後の確認はブラウザの確認ダイアログで行う（2026-10-10 に利用者が決めた）。
 * 画面の注意書きを読み飛ばして赤いボタンを押しても、もう 1 段止まる。
 */
export const confirmMessage = (systemTitle: string): string =>
    `「${systemTitle}」を解約します。この操作は元に戻せません。よろしいですか？`;

function CancelForm({ value }: { readonly value: TenantCancellation }): ReactElement {
    usePageMeta(title);
    const form = useSend(tenantApi.cancel(value.tenantId));

    const onSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
        event.preventDefault();
        if (!globalThis.confirm(confirmMessage(value.title))) {
            return;
        }
        form.send(new URLSearchParams());
    };

    return (
        <div className="page max-w-lg">
            <div className="panel">
                <h1 className="page-title">{title}</h1>
                <p className="page-lead">{value.title}</p>

                <FormSummary messages={form.summary} />

                <div className="alert-warning mb-7 whitespace-pre-line">{value.confirmText}</div>

                <div className="flex gap-3">
                    <a href={tenantUrls.list} className="btn-secondary grow">キャンセル</a>
                    <form method="post" className="grow" onSubmit={onSubmit}>
                        <button type="submit" className="btn-danger w-full" disabled={form.busy}>解約する</button>
                    </form>
                </div>
            </div>
        </div>
    );
}

/** 解約。<b>契約者だけ</b>——それ以外は API が 404 を返す。終われば契約一覧で知らせる（API の notice）。 */
export function CancelPage(): ReactElement {
    const tenantId = useParams()["tenantId"] ?? "";
    const data = useApiData<TenantCancellation>(tenantApi.cancel(tenantId));

    return data.state === "ready"
        ? <CancelForm value={data.value} />
        : <LoadingPage title={title} data={data} width="max-w-lg" />;
}
